/** Operator metadata helpers for CAP / jnet2 Node devices. */

export const MAX_DEVICE_DISPLAY_NAME_LEN = 128;

export function getDeviceDisplayName(device) {
  const name = String(device?.display_name || device?.name || '').trim();
  const serial = device?.serial_number || '';
  return name || serial;
}

export function formatDeviceLabel(device) {
  const name = String(device?.display_name || device?.name || '').trim();
  const serial = device?.serial_number || '';
  if (name && serial && name.toLowerCase() !== serial.toLowerCase()) {
    return `${name} · ${serial}`;
  }
  return serial || name;
}

function parseCoordinate(value) {
  if (value == null || value === '') return null;
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(num) ? num : null;
}

/** Effective map GPS: operator override wins over device-reported GPS. */
export function getEffectiveGps(device) {
  const override = device?.gps_override;
  if (override) {
    const lat = parseCoordinate(override.lat ?? override.latitude);
    const lng = parseCoordinate(override.lng ?? override.longitude);
    if (lat != null && lng != null && !(lat === 0 && lng === 0)) {
      return { lat, lng, alt: override.alt ?? override.altitude ?? null, source: 'override' };
    }
  }

  const reported = device?.gps || device?.gps_location;
  if (reported) {
    const lat = parseCoordinate(reported.lat ?? reported.latitude);
    const lng = parseCoordinate(reported.lng ?? reported.longitude);
    if (lat != null && lng != null && !(lat === 0 && lng === 0)) {
      return {
        lat,
        lng,
        alt: reported.alt ?? reported.altitude ?? null,
        source: device?.gps_source || 'reported',
      };
    }
  }

  return null;
}

/** Device-reported GPS only (ignores operator gps_override). */
export function getReportedDeviceGps(device) {
  const reported = device?.gps || device?.gps_location;
  if (!reported) return null;
  const lat = parseCoordinate(reported.lat ?? reported.latitude);
  const lng = parseCoordinate(reported.lng ?? reported.longitude);
  if (lat == null || lng == null || (lat === 0 && lng === 0)) {
    return null;
  }
  const altRaw = reported.alt ?? reported.altitude;
  const alt = altRaw != null && altRaw !== '' ? parseCoordinate(altRaw) : null;
  return { lat, lng, alt: Number.isFinite(alt) ? alt : null };
}

/** True when device has live-reported GPS that can be frozen as manual override. */
export function canLockCurrentDeviceGps(device) {
  if (hasManualGpsOverride(device)) return false;
  return getReportedDeviceGps(device) != null;
}

export function formatGpsCoordinates(gps) {
  if (!gps) return '';
  const lat = typeof gps.lat === 'number' ? gps.lat.toFixed(6) : gps.lat;
  const lng = typeof gps.lng === 'number' ? gps.lng.toFixed(6) : gps.lng;
  return `${lat}, ${lng}`;
}

export function hasManualGpsOverride(device) {
  return getEffectiveGps(device)?.source === 'override'
    || (device?.gps_override && (device.gps_override.lat || device.gps_override.lng));
}

export function gpsSourceLabel(source) {
  switch (source) {
    case 'override':
      return 'Manual GPS';
    case 'reported':
      return 'Device GPS';
    case 'inherited':
      return 'Inherited GPS';
    default:
      return '';
  }
}

/** Normalize a MAC for nickname suffixes (lowercase, no separators). */
export function normalizeMacForNickname(mac) {
  if (mac == null || mac === '') return '';
  return String(mac).trim().toLowerCase().replace(/[^a-f0-9]/g, '');
}

/** Jnet2 device MAC for nickname suffix (device serial). */
export function getDeviceMacForNickname(device) {
  const serial = device?.serial_number;
  if (!serial) return '';
  return String(serial).trim();
}

/** Build display name as `{prefix}_{normalizedMac}`. */
export function buildDeviceNickname(prefix, device) {
  const base = String(prefix || '').trim();
  const macRaw = getDeviceMacForNickname(device);
  const mac = normalizeMacForNickname(macRaw);
  if (!base || !mac) return null;
  const nickname = `${base}_${mac}`;
  if (nickname.length > MAX_DEVICE_DISPLAY_NAME_LEN) {
    return { nickname: null, macRaw, mac, tooLong: true };
  }
  return { nickname, macRaw, mac, tooLong: false };
}
